import { logEditorCorrelation } from "../../utils/editor";

const isDev = process.env.NODE_ENV === "development";

export const SPACE_SWITCH_REMOUNT_COOLDOWN_MS = 5000;
export const SPACE_SWITCH_COMPLETION_COOLDOWN_MS = 1500;
/** 전환이 `completed`가 된 직후 한 번 발행한다. 전환 중 미룬 작업(Import 재개 등)을 다시 판정하는 신호다. */
export const SPACE_SWITCH_COMPLETED_EVENT = "viewr:spaceSwitchCompleted";

let completionReleaseTimer: ReturnType<typeof setTimeout> | null = null;
let transactionCounter = 0;
let lastRemountBlockIgnoredSignature: string | null = null;
const preflightPersistedTransactionIds = new Set<string>();
// ponytail: 세션 동안 시간 초과 transaction만 쌓인다(사용자 재시도 횟수만큼). 커지면 LRU로 자른다.
const timedOutTransactionIds = new Set<string>();

/** 로딩·dim 60초 초과로 포기한 Space transaction. 늦게 도착한 Unity 이벤트를 이 transaction으로 확정하지 않는다 (QA-445 LOAD-05·06). */
export function markSpaceSwitchTimedOut(transactionId: string): void {
  timedOutTransactionIds.add(transactionId);
}

export type SpaceSwitchTransactionType =
  | "space_switch"
  | "remount_only"
  | "space_add"
  | "space_duplicate"
  | "project_load";
export type SpaceSwitchRestoreMode = "rehydrate" | "reload";

export type SpaceSwitchStage =
  | "pending_save"
  | "preflight_save"
  | "unloading"
  | "remounting"
  | "restoring_project"
  | "restoring_runtime_state"
  | "restore_ready"
  | "selecting_space"
  | "awaiting_space_confirmation"
  | "interaction_ready"
  | "stabilizing"
  | "completed"
  | "aborted";

export function isTerminalSpaceSwitchStage(
  stage: SpaceSwitchStage | null | undefined,
): boolean {
  return (
    stage === "interaction_ready" ||
    stage === "completed" ||
    stage === "aborted"
  );
}

type WindowSpaceSwitchState = {
  active: boolean;
  transactionId: string | null;
  transactionType: SpaceSwitchTransactionType | null;
  stage: SpaceSwitchStage | null;
  startedAt: number | null;
  targetSpaceIndex: number | null;
  runId: number | null;
  restoreMode: SpaceSwitchRestoreMode | null;
  completedAt: number | null;
};

export type SpaceSwitchFreshnessDetail = {
  transactionId?: string | null;
  runId?: number | null;
  targetSpaceIndex?: number | null;
};

export type SpaceSwitchFreshnessOptions = {
  source?: string;
  allowLegacyNoTransaction?: boolean;
  allowMissingRunId?: boolean;
  allowMissingTargetSpaceIndex?: boolean;
};

const allowedStageTransitions: Record<SpaceSwitchStage, SpaceSwitchStage[]> = {
  pending_save: ["pending_save", "preflight_save", "unloading", "aborted"],
  preflight_save: ["preflight_save", "unloading", "aborted"],
  unloading: ["unloading", "remounting", "aborted"],
  remounting: ["remounting", "restoring_project", "aborted"],
  restoring_project: ["restoring_project", "restore_ready", "aborted"],
  restoring_runtime_state: [
    "restoring_runtime_state",
    "restore_ready",
    "aborted",
  ],
  restore_ready: [
    "restore_ready",
    "selecting_space",
    "awaiting_space_confirmation",
    "interaction_ready",
    "aborted",
  ],
  selecting_space: [
    "selecting_space",
    "awaiting_space_confirmation",
    "interaction_ready",
    "aborted",
  ],
  awaiting_space_confirmation: [
    "awaiting_space_confirmation",
    "interaction_ready",
    "stabilizing",
    "aborted",
  ],
  interaction_ready: [
    "interaction_ready",
    "stabilizing",
    "completed",
    "aborted",
  ],
  stabilizing: ["stabilizing", "completed", "interaction_ready", "aborted"],
  completed: ["completed"],
  aborted: ["aborted"],
};

export interface TransactionAwareUnitySyncPayload {
  reason: string;
  sourceReason: string;
  transactionId: string | null;
  targetSpaceIndex: number | null;
  isSpaceSwitchPayload: boolean;
}

export interface BuildTransactionAwareUnitySyncPayloadArgs {
  source: string;
  reason?: string;
  transactionId?: string | null;
  targetSpaceIndex?: number | null;
  allowDuringSpaceSwitch?: boolean;
  respectRemountCooldown?: boolean;
  additionalPayload?: Record<string, unknown>;
}

function clearCompletionReleaseTimer(): void {
  if (completionReleaseTimer) {
    clearTimeout(completionReleaseTimer);
    completionReleaseTimer = null;
  }
}

function createTransactionId(): string {
  transactionCounter += 1;
  return `space_switch_${Date.now()}_${transactionCounter}`;
}

function readWindowState(): WindowSpaceSwitchState {
  if (typeof window === "undefined") {
    return {
      active: false,
      transactionId: null,
      transactionType: null,
      stage: null,
      startedAt: null,
      targetSpaceIndex: null,
      runId: null,
      restoreMode: null,
      completedAt: null,
    };
  }

  return {
    active: window.__viewr_spaceSwitchActive === true,
    transactionId: window.__viewr_spaceSwitchTransactionId ?? null,
    transactionType: window.__viewr_spaceSwitchTransactionType ?? null,
    stage: window.__viewr_spaceSwitchStage ?? null,
    startedAt:
      typeof window.__viewr_spaceSwitchStartedAt === "number"
        ? window.__viewr_spaceSwitchStartedAt
        : null,
    targetSpaceIndex: window.__viewr_spaceSwitchTargetIndex ?? null,
    runId:
      typeof window.__viewr_spaceSwitchRunId === "number"
        ? window.__viewr_spaceSwitchRunId
        : null,
    restoreMode: window.__viewr_spaceSwitchRestoreMode ?? null,
    completedAt:
      typeof window.__viewr_spaceSwitchCompletedAt === "number"
        ? window.__viewr_spaceSwitchCompletedAt
        : null,
  };
}

function updateWindowState(partial: Partial<WindowSpaceSwitchState>): void {
  if (typeof window === "undefined") return;

  if (typeof partial.active === "boolean") {
    window.__viewr_spaceSwitchActive = partial.active;
  }
  if ("transactionId" in partial) {
    window.__viewr_spaceSwitchTransactionId = partial.transactionId ?? null;
  }
  if ("transactionType" in partial) {
    window.__viewr_spaceSwitchTransactionType = partial.transactionType ?? null;
  }
  if ("stage" in partial) {
    window.__viewr_spaceSwitchStage = partial.stage ?? undefined;
    window.__viewr_spaceSwitchPhase = partial.stage ?? undefined;
  }
  if ("startedAt" in partial) {
    window.__viewr_spaceSwitchStartedAt = partial.startedAt ?? undefined;
  }
  if ("targetSpaceIndex" in partial) {
    window.__viewr_spaceSwitchTargetIndex = partial.targetSpaceIndex ?? null;
  }
  if ("runId" in partial) {
    window.__viewr_spaceSwitchRunId = partial.runId ?? null;
  }
  if ("restoreMode" in partial) {
    window.__viewr_spaceSwitchRestoreMode = partial.restoreMode ?? null;
  }
  if ("completedAt" in partial) {
    window.__viewr_spaceSwitchCompletedAt = partial.completedAt ?? null;
  }
}

function matchesActiveTransaction(
  transactionId: string | null | undefined,
): boolean {
  if (!transactionId) return false;
  return readWindowState().transactionId === transactionId;
}

function getCompletionCooldownReason(): string | null {
  const state = readWindowState();
  if (state.transactionType === "project_load") {
    return null;
  }
  if (state.stage !== "completed" || typeof state.completedAt !== "number") {
    return null;
  }
  if (Date.now() - state.completedAt >= SPACE_SWITCH_COMPLETION_COOLDOWN_MS) {
    return null;
  }
  return "space_switch_completion_cooldown";
}

function isBlockingSpaceSwitchState(state: WindowSpaceSwitchState): boolean {
  return (
    state.active &&
    state.transactionId !== null &&
    state.transactionType !== "project_load" &&
    !isTerminalSpaceSwitchStage(state.stage)
  );
}

export function markSpaceSwitchPreflightPersisted(
  transactionId: string | null | undefined,
): void {
  if (!transactionId) return;
  preflightPersistedTransactionIds.add(transactionId);
}

export function wasSpaceSwitchPreflightPersisted(
  transactionId: string | null | undefined,
): boolean {
  if (!transactionId) return false;
  return preflightPersistedTransactionIds.has(transactionId);
}

function clearSpaceSwitchPreflightPersisted(
  transactionId: string | null | undefined,
): void {
  if (transactionId) {
    preflightPersistedTransactionIds.delete(transactionId);
    return;
  }
  preflightPersistedTransactionIds.clear();
}

/** Post-navigation autosave (B) runs only when regression guard needs it or preflight did not persist. */
export function shouldSkipRedundantSpaceSwitchPostAutosave(args: {
  transactionId: string | null | undefined;
  hasModelChanges: boolean;
  persistenceRegressed: boolean;
}): boolean {
  if (args.persistenceRegressed) {
    return false;
  }
  if (!args.hasModelChanges) {
    return true;
  }
  return wasSpaceSwitchPreflightPersisted(args.transactionId);
}

export function beginSpaceSwitch(
  targetSpaceIndex?: number | null,
  transactionType: SpaceSwitchTransactionType = "space_switch",
  adoptTransactionId?: string | null,
): {
  transactionId: string;
} {
  clearCompletionReleaseTimer();
  // 호출자가 복원 전에 만든 transaction ID(예: editor import reload)를 그대로 소유권으로
  // 등록한다. 소유자가 없는 navigation은 canonical 완료가 전부 무시되어 UI terminal이
  // 성립하지 않는다.
  const transactionId =
    typeof adoptTransactionId === "string" && adoptTransactionId.trim()
      ? adoptTransactionId.trim()
      : createTransactionId();
  updateWindowState({
    active: true,
    transactionId,
    transactionType,
    stage:
      transactionType === "space_switch"
        ? "pending_save"
        : transactionType === "space_add" ||
            transactionType === "space_duplicate"
          ? "restore_ready"
          : transactionType === "project_load"
            ? "restoring_project"
            : "remounting",
    startedAt: Date.now(),
    targetSpaceIndex: targetSpaceIndex ?? null,
    runId: null,
    restoreMode: null,
    completedAt: null,
  });

  if (isDev) {
    console.log("[SpaceSwitch] begin", {
      transactionId,
      transactionType,
      targetSpaceIndex: targetSpaceIndex ?? null,
    });
  }

  return { transactionId };
}

export function setSpaceSwitchTargetIndex(
  targetSpaceIndex: number | null | undefined,
): void {
  updateWindowState({
    targetSpaceIndex: targetSpaceIndex ?? null,
  });
}

export function setSpaceSwitchRunId(runId: number | null | undefined): void {
  updateWindowState({
    runId: typeof runId === "number" ? runId : null,
  });
}

export function setSpaceSwitchRestoreMode(
  restoreMode: SpaceSwitchRestoreMode | null | undefined,
): void {
  updateWindowState({
    restoreMode: restoreMode ?? null,
  });
}

export function setSpaceSwitchStage(
  transactionId: string,
  stage: SpaceSwitchStage,
  detail?: Record<string, unknown>,
): boolean {
  if (!matchesActiveTransaction(transactionId)) return false;
  const currentStage = readWindowState().stage;
  if (currentStage && currentStage !== stage) {
    const allowedNextStages = allowedStageTransitions[currentStage] ?? [];
    if (!allowedNextStages.includes(stage)) {
      logEditorCorrelation(
        "space_switch_stage_transition_rejected",
        {
          phase: "stage_transition",
          source: "spaceSwitchState",
        },
        {
          transactionId,
          currentStage,
          requestedStage: stage,
          ...(detail ?? {}),
        },
        "warn",
      );
      return false;
    }
  }
  updateWindowState({ stage });

  if (isDev) {
    console.log(`[SpaceSwitch] stage=${stage}`, {
      transactionId,
      ...(detail ?? {}),
    });
  }
  return true;
}

export function completeSpaceSwitch(
  transactionId: string,
  detail?: Record<string, unknown>,
): void {
  if (!matchesActiveTransaction(transactionId)) return;

  clearCompletionReleaseTimer();
  const completedAt = Date.now();
  updateWindowState({
    active: true,
    stage: "completed",
    completedAt,
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SPACE_SWITCH_COMPLETED_EVENT, { detail: { transactionId } }));
  }

  completionReleaseTimer = setTimeout(() => {
    const state = readWindowState();
    if (
      state.transactionId !== transactionId ||
      state.stage !== "completed" ||
      state.completedAt !== completedAt
    ) {
      return;
    }

    clearSpaceSwitchPreflightPersisted(transactionId);
    updateWindowState({
      active: false,
      transactionId: null,
      transactionType: null,
      targetSpaceIndex: null,
      runId: null,
      restoreMode: null,
    });
    completionReleaseTimer = null;
  }, SPACE_SWITCH_COMPLETION_COOLDOWN_MS);

  if (isDev) {
    console.log("[SpaceSwitch] stage=completed", {
      transactionId,
      ...(detail ?? {}),
    });
  }
}

export function softCompleteSpaceSwitch(
  transactionId: string,
  detail?: Record<string, unknown>,
): void {
  if (!matchesActiveTransaction(transactionId)) return;

  clearCompletionReleaseTimer();
  updateWindowState({
    active: false,
    stage: "interaction_ready",
    completedAt: null,
  });

  if (isDev) {
    console.log("[SpaceSwitch] stage=interaction_ready soft-completed", {
      transactionId,
      ...(detail ?? {}),
    });
  }
}

export function abortSpaceSwitch(
  transactionId: string | null | undefined,
  detail?: Record<string, unknown>,
): void {
  if (transactionId && !matchesActiveTransaction(transactionId)) {
    return;
  }

  clearCompletionReleaseTimer();
  clearSpaceSwitchPreflightPersisted(transactionId ?? null);
  updateWindowState({
    active: false,
    transactionId: null,
    transactionType: null,
    stage: "aborted",
    targetSpaceIndex: null,
    runId: null,
    restoreMode: null,
    completedAt: null,
  });

  if (isDev) {
    console.warn("[SpaceSwitch] stage=aborted", {
      transactionId: transactionId ?? null,
      ...(detail ?? {}),
    });
  }
}

export function isSpaceSwitchActive(): boolean {
  return isBlockingSpaceSwitchState(readWindowState());
}

export function getActiveSpaceSwitchTransactionId(): string | null {
  return readWindowState().transactionId;
}

export function getActiveSpaceSwitchTargetIndex(): number | null {
  return readWindowState().targetSpaceIndex;
}

export function getActiveSpaceSwitchRunId(): number | null {
  return readWindowState().runId;
}

export function getActiveSpaceSwitchRestoreMode(): SpaceSwitchRestoreMode | null {
  return readWindowState().restoreMode;
}

export function getActiveSpaceSwitchStage(): SpaceSwitchStage | null {
  return readWindowState().stage;
}

export function getActiveSpaceSwitchTransactionType(): SpaceSwitchTransactionType | null {
  return readWindowState().transactionType;
}

export function isSpaceSwitchStage(...stages: SpaceSwitchStage[]): boolean {
  const stage = getActiveSpaceSwitchStage();
  return stage != null && stages.includes(stage);
}

export function isActiveSpaceSwitchTransaction(
  transactionId: string | null | undefined,
): boolean {
  return matchesActiveTransaction(transactionId);
}

function isEditorImportReloadCorrelationTransaction(
  transactionId: string | null | undefined,
): boolean {
  return (
    typeof transactionId === "string" &&
    transactionId.startsWith("editor-import-reload:")
  );
}

export function isLatestSpaceSwitchEvent(
  detail: SpaceSwitchFreshnessDetail,
  options?: SpaceSwitchFreshnessOptions,
): boolean {
  const state = readWindowState();
  const source = options?.source ?? "unknown";
  const eventTransactionId = detail.transactionId ?? null;

  if (isEditorImportReloadCorrelationTransaction(eventTransactionId)) {
    return true;
  }

  if (eventTransactionId && timedOutTransactionIds.has(eventTransactionId)) {
    logEditorCorrelation(
      "space_switch_stale_event_ignored",
      { phase: "freshness", source },
      { reason: "transaction_timed_out", eventTransactionId },
      "warn",
    );
    return false;
  }

  if (!eventTransactionId) {
    // 레거시(트랜잭션 ID 없음): 진행 중 스위치가 있으면 그 트랜잭션과 묶어 처리하고,
    // idle(aborted/완료 후 정리됨)에서는 트랜잭션이 없어도 허용한다. 그렇지 않으면
    // prepare 실패 직후 softCompleted 등이 무시되어 orchestrator 큐/언락이 막힌다.
    const accepted =
      options?.allowLegacyNoTransaction === true &&
      (state.transactionId !== null || !state.active);
    logEditorCorrelation(
      accepted
        ? "space_switch_legacy_event_accepted"
        : "space_switch_legacy_event_ignored",
      {
        phase: "freshness",
        source,
      },
      {
        activeTransactionId: state.transactionId,
        activeRunId: state.runId,
        activeTargetSpaceIndex: state.targetSpaceIndex,
        eventRunId: detail.runId ?? null,
        eventTargetSpaceIndex: detail.targetSpaceIndex ?? null,
      },
      accepted ? "log" : "warn",
    );
    return accepted;
  }

  if (!state.transactionId) {
    logEditorCorrelation(
      "space_switch_legacy_event_accepted",
      {
        phase: "freshness",
        source,
      },
      {
        legacyNoActiveTransaction: true,
        eventTransactionId,
        eventRunId: detail.runId ?? null,
        eventTargetSpaceIndex: detail.targetSpaceIndex ?? null,
      },
    );
    return true;
  }

  if (state.transactionId !== eventTransactionId) {
    logEditorCorrelation(
      "space_switch_stale_event_ignored",
      {
        phase: "freshness",
        source,
      },
      {
        reason: "transaction_mismatch",
        activeTransactionId: state.transactionId,
        eventTransactionId,
        activeRunId: state.runId,
        eventRunId: detail.runId ?? null,
        activeTargetSpaceIndex: state.targetSpaceIndex,
        eventTargetSpaceIndex: detail.targetSpaceIndex ?? null,
      },
      "warn",
    );
    return false;
  }

  if (
    typeof detail.runId === "number" &&
    typeof state.runId === "number" &&
    detail.runId !== state.runId
  ) {
    logEditorCorrelation(
      "space_switch_stale_event_ignored",
      {
        phase: "freshness",
        source,
      },
      {
        reason: "run_id_mismatch",
        activeTransactionId: state.transactionId,
        eventTransactionId,
        activeRunId: state.runId,
        eventRunId: detail.runId,
      },
      "warn",
    );
    return false;
  }

  if (
    options?.allowMissingRunId !== true &&
    detail.runId == null &&
    typeof state.runId === "number"
  ) {
    logEditorCorrelation(
      "space_switch_legacy_event_accepted",
      {
        phase: "freshness",
        source,
      },
      {
        legacyNoRunId: true,
        activeTransactionId: state.transactionId,
        activeRunId: state.runId,
        eventTargetSpaceIndex: detail.targetSpaceIndex ?? null,
      },
    );
  }

  if (
    typeof detail.targetSpaceIndex === "number" &&
    typeof state.targetSpaceIndex === "number" &&
    detail.targetSpaceIndex !== state.targetSpaceIndex
  ) {
    logEditorCorrelation(
      "space_switch_stale_event_ignored",
      {
        phase: "freshness",
        source,
      },
      {
        reason: "target_space_mismatch",
        activeTransactionId: state.transactionId,
        eventTransactionId,
        activeTargetSpaceIndex: state.targetSpaceIndex,
        eventTargetSpaceIndex: detail.targetSpaceIndex,
      },
      "warn",
    );
    return false;
  }

  if (
    options?.allowMissingTargetSpaceIndex !== true &&
    detail.targetSpaceIndex == null &&
    typeof state.targetSpaceIndex === "number"
  ) {
    logEditorCorrelation(
      "space_switch_legacy_event_accepted",
      {
        phase: "freshness",
        source,
      },
      {
        legacyNoTargetSpaceIndex: true,
        activeTransactionId: state.transactionId,
        activeTargetSpaceIndex: state.targetSpaceIndex,
        eventRunId: detail.runId ?? null,
      },
    );
  }

  return true;
}

export function isSpaceSwitchCompletionCoolingDown(): boolean {
  return getCompletionCooldownReason() !== null;
}

export function getSpaceSwitchRequestBlockReason(): string | null {
  const state = readWindowState();

  if (isBlockingSpaceSwitchState(state)) {
    lastRemountBlockIgnoredSignature = null;
    return `space_switch_active:${state.stage ?? "unknown"}`;
  }

  if (typeof window !== "undefined" && window.__viewr_remountInProgress) {
    if (isTerminalSpaceSwitchStage(state.stage)) {
      const signature = `${state.transactionId ?? "none"}:${state.stage}`;
      if (isDev && lastRemountBlockIgnoredSignature !== signature) {
        lastRemountBlockIgnoredSignature = signature;
        console.log("[SpaceSwitch] remount block ignored for terminal stage", {
          event: "remount_block_ignored_for_terminal_stage",
          transactionId: state.transactionId,
          stage: state.stage,
          targetSpaceIndex: state.targetSpaceIndex,
        });
      }
      return null;
    }
    lastRemountBlockIgnoredSignature = null;
    return "remount_in_progress";
  }

  lastRemountBlockIgnoredSignature = null;

  return null;
}

export function shouldBlockNewSpaceSwitchRequest(): boolean {
  return getSpaceSwitchRequestBlockReason() !== null;
}

export function shouldSuppressAutomaticUnitySync(args: {
  source: string;
  reason?: string;
  respectRemountCooldown?: boolean;
}): boolean {
  if (typeof window === "undefined") return false;

  const {
    source,
    reason = "unspecified",
    respectRemountCooldown = true,
  } = args;

  let suppressReason: string | null = null;

  if (
    window.__viewr_spaceSwitchActive &&
    window.__viewr_spaceSwitchTransactionType !== "project_load" &&
    !isTerminalSpaceSwitchStage(window.__viewr_spaceSwitchStage ?? null)
  ) {
    suppressReason = `space_switch_active:${window.__viewr_spaceSwitchStage ?? "unknown"}`;
  } else if (getCompletionCooldownReason()) {
    suppressReason = getCompletionCooldownReason();
  } else if (
    window.__viewr_remountInProgress &&
    !isTerminalSpaceSwitchStage(window.__viewr_spaceSwitchStage ?? null)
  ) {
    suppressReason = "remount_in_progress";
  } else if (
    respectRemountCooldown &&
    typeof window.__viewr_remountCompletedAt === "number" &&
    Date.now() - window.__viewr_remountCompletedAt <
      SPACE_SWITCH_REMOUNT_COOLDOWN_MS
  ) {
    suppressReason = "remount_cooldown";
  }

  if (!suppressReason) {
    return false;
  }

  if (isDev) {
    console.log("[SpaceSwitch] suppressed requestLatestViewRData", {
      source,
      reason,
      suppressReason,
    });
  }
  return true;
}

export function shouldSuppressSpaceSwitchAutosave(source: string): boolean {
  const suppressReason = isSpaceSwitchActive()
    ? `space_switch_active:${getActiveSpaceSwitchStage() ?? "unknown"}`
    : getCompletionCooldownReason();

  if (suppressReason && isDev) {
    console.log("[SpaceSwitch] suppressed autosave fallback", {
      source,
      suppressReason,
      stage: getActiveSpaceSwitchStage() ?? "unknown",
      targetSpaceIndex: getActiveSpaceSwitchTargetIndex(),
    });
  }
  return Boolean(suppressReason);
}

export function buildTransactionAwareUnitySyncPayload({
  source,
  reason = "unspecified",
  transactionId,
  targetSpaceIndex,
  allowDuringSpaceSwitch = false,
  respectRemountCooldown = true,
  additionalPayload,
}: BuildTransactionAwareUnitySyncPayloadArgs):
  (TransactionAwareUnitySyncPayload & Record<string, unknown>) | null {
  const activeTransactionId = getActiveSpaceSwitchTransactionId();
  const activeTargetSpaceIndex = getActiveSpaceSwitchTargetIndex();
  const resolvedTransactionId = transactionId ?? activeTransactionId;
  const resolvedTargetSpaceIndex =
    typeof targetSpaceIndex === "number"
      ? targetSpaceIndex
      : activeTargetSpaceIndex;

  if (allowDuringSpaceSwitch) {
    if (isSpaceSwitchActive() && !resolvedTransactionId) {
      if (isDev) {
        console.warn("[SpaceSwitch] suppress unity sync: missing transaction", {
          source,
          reason,
        });
      }
      return null;
    }

    if (
      activeTransactionId &&
      resolvedTransactionId &&
      activeTransactionId !== resolvedTransactionId
    ) {
      if (isDev) {
        console.warn("[SpaceSwitch] suppress unity sync: stale transaction", {
          source,
          reason,
          activeTransactionId,
          resolvedTransactionId,
        });
      }
      return null;
    }
  } else if (
    shouldSuppressAutomaticUnitySync({
      source,
      reason,
      respectRemountCooldown,
    })
  ) {
    return null;
  }

  return {
    reason,
    sourceReason: source,
    transactionId: resolvedTransactionId ?? null,
    targetSpaceIndex: resolvedTargetSpaceIndex ?? null,
    isSpaceSwitchPayload: allowDuringSpaceSwitch && !!resolvedTransactionId,
    ...(additionalPayload ?? {}),
  };
}
